import { RefObject } from 'react';
import { styled } from '@mui/material/styles';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import {
  ComplianceCoverageFileType,
  ComplianceCoverageMarket,
  ComplianceCoverageRow,
} from '@/lib/types';
import ComplianceCoverageCell from './ComplianceCoverageCell';
import { getMarketLabel } from './ComplianceCoverageAdminPageHelpers';

type ComplianceCoverageGridProps = {
  rows: ComplianceCoverageRow[];
  markets: ComplianceCoverageMarket[];
  fileType: ComplianceCoverageFileType;
  returnTo: string;
  scrollRef: RefObject<HTMLDivElement | null>;
  loadingMore: boolean;
};

const ComplianceCoverageGrid = ({
  rows,
  markets,
  fileType,
  returnTo,
  scrollRef,
  loadingMore,
}: ComplianceCoverageGridProps) => (
  <Scroll ref={scrollRef} className="wide-scrollbar">
    <StyledTable aria-label="Compliance coverage">
      <TableHead>
        <TableRow>
          <CornerCell>
            <FormattedMessage
              id="complianceCoverage.column.game"
              defaultMessage="Game / RTP"
            />
          </CornerCell>
          {markets.map((market) => (
            <MarketHeadCell key={market.id}>
              {getMarketLabel(market.name)}
            </MarketHeadCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <TableRow key={row.game_id}>
            <VariantCell component="th" scope="row">
              <VariantName>{row.game_name}</VariantName>
              {row.studio_game_id ? (
                <StudioGameId>{row.studio_game_id}</StudioGameId>
              ) : null}
            </VariantCell>
            {markets.map((market) => (
              <ComplianceCoverageCell
                key={market.id}
                row={row}
                market={market}
                fileType={fileType}
                returnTo={returnTo}
              />
            ))}
          </TableRow>
        ))}
        {loadingMore ? (
          <TableRow>
            <LoadingCell colSpan={markets.length + 1}>
              <FormattedMessage
                id="loading"
                defaultMessage="Loading..."
              />
            </LoadingCell>
          </TableRow>
        ) : null}
      </TableBody>
    </StyledTable>
  </Scroll>
);

const Scroll = styled('div')({
  flex: 1,
  minHeight: 0,
  overflow: 'auto',
});

const StyledTable = styled(Table)({
  width: 'max-content',
  minWidth: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
});

const HeadCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  zIndex: 2,
  padding: theme.spacing(1, 1.25),
  background: theme.palette.grey50,
  borderBottom: `1px solid ${theme.palette.grey200}`,
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap',
  color: theme.palette.textSecondaryDarkGrey,
}));

const MarketHeadCell = styled(HeadCell)({
  textAlign: 'center',
});

const CornerCell = styled(HeadCell)(({ theme }) => ({
  left: 0,
  zIndex: 3,
  borderRight: `1px solid ${theme.palette.grey200}`,
}));

const VariantCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  zIndex: 1,
  minWidth: 270,
  padding: theme.spacing(1, 1.25),
  background: theme.palette.white,
  borderRight: `1px solid ${theme.palette.grey200}`,
  borderBottom: `1px solid ${theme.palette.grey200}`,
  whiteSpace: 'nowrap',
}));

const VariantName = styled('span')(({ theme }) => ({
  fontSize: 14,
  fontWeight: 600,
  color: theme.palette.textPrimaryBlack,
}));

const StudioGameId = styled('span')(({ theme }) => ({
  marginLeft: theme.spacing(0.75),
  fontFamily: 'ui-monospace, Menlo, monospace',
  fontSize: 12,
  color: theme.palette.grey500,
}));

const LoadingCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(2),
  textAlign: 'center',
  color: theme.palette.textSecondaryDarkGrey,
}));

export default ComplianceCoverageGrid;
